import { inject, Injectable } from '@angular/core';
import { loadAllAccountProjects } from './../../../app/@store/account/account.actions';
import { CheckboxVisibility, ExtendedConfirmationOptions, OverlayAlertService } from '@gridscale/ingrid/components/overlay-alert';
import { Store } from '@ngrx/store';
import { ofType, Actions } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

import * as _ from 'lodash';
import { concatMap, filter, map, take, withLatestFrom, takeUntil } from 'rxjs/operators';
import { NgrxService } from './ngrx.service';
import { ToastService } from './toast.service';
import * as AccountActions from './../../../app/@store/account/account.actions';
import * as AccountSelectors from './../../../app/@store/account/account.selectors';
import * as UserActions from './../../../app/@store/user/user.actions';
import * as UserSelectors from './../../../app/@store/user/user.selectors';
import * as CurrentUserSelectors from './../../../app/@store/current-user/current-user.selectors';
import * as StatsSelectors from '../../@store/stats/stats.selectors';
import * as StatsActions from '../../@store/stats/stats.actions';
import { User } from '../models/user.model';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { BackgroundJobService, JOB_STATUS } from '@gridscale/ingrid/components/background-job';
import { AccountProjectRelation } from '../models/accounts_response.model';
import { Account } from '../models/account.model';
import { contractmanagement_api } from '@gridscale/gs-services/contractmanagement_api'; import * as ContractActions from "../../@store/account/account.actions";
import {
  AccountAssignComponent
} from "../../panel/users/_user-details/accounts/account-assign/account-assign.component";
import { AccountAddComponent } from "../../panel/accounts/_add/account-add.component";
import { toSignal } from "@angular/core/rxjs-interop";
import { AutocompleteSuggestion } from '@gridscale/ingrid/components/autocomplete';


@Injectable({
  providedIn: 'root'
})
export class AccountService {

  readonly #store = inject(Store);
  readonly #overlayAlert = inject(OverlayAlertService);
  readonly #translate = inject(TranslateService);
  readonly #toastService = inject(ToastService);
  readonly #ngrxService = inject(NgrxService);
  readonly #backgroundJobService = inject(BackgroundJobService);
  readonly #actions$ = inject(Actions);
  readonly #accountsTotal = toSignal(this.#store.select(AccountSelectors.getAccountsTotal), { initialValue: 0 });

  getAccountProject$ = this.#store.select(AccountSelectors.getAccountProjects);
  getAccountRelations$ = this.#store.select(AccountSelectors.getAccountRelations);
  isAccountProjectsLoaded$ = this.#store.select(AccountSelectors.isAccountProjectsLoaded);

  readonly isViewBusy$ = new BehaviorSubject<boolean>(false);
  private readonly errorOccured$: Subject<void> = new Subject();

  projectsCache: Record<string, AccountProjectRelation> = {};
  accountRelationsCache: ReturnType<typeof AccountSelectors.getAccountRelations> = {};
  public projectsDataLoaded = false;

  constructor() {
    // Create Legacy fetcher for SDN Data. Try to load the relevant Data we need to work with accounts
    this.#store.dispatch(loadAllAccountProjects());
    this.getAccountProject$
      .pipe(
        filter(_projects => _projects !== undefined && _.size(_projects) > 0),
        map(projects => {
          this.projectsCache = projects;
        })
      )
      .subscribe();
    this.getAccountRelations$
      .pipe(
        filter(_relations => _relations !== undefined && _.size(_relations) > 0),
        map(relations => {
          this.accountRelationsCache = relations;
        })
      )
      .subscribe();

    this.isAccountProjectsLoaded$
      .pipe(
        map((projectsDataLoaded: any) => {
          this.projectsDataLoaded = projectsDataLoaded;
        })
      )
      .subscribe();
  }

  public showRelateAccountOverlay(user: Partial<User>|undefined) {
    if (!user) {
      return;
    }
    if (this.#accountsTotal() > 0) {
      this.#overlayAlert.custom({
        headline: this.#translate.instant('account_relate_to_user', { email: user.email }),
        iconCls: 'icon-a147_add_larger',
        buttons: [
          {
            label: this.#translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alert, childComponentRef) => childComponentRef?.instance.no()
          },
          {
            label: this.#translate.instant('relate_account'),
            mode: 'primary',
            disabled: true,
            callback: (button, alert, childComponentRef) => {
              childComponentRef?.instance.yes();
              this.#store.dispatch(UserActions.loadUser({ user_uuid: user.object_uuid! }));
            }
          }
        ],
        width: '800px',
        advancedAlignmentMode: true
      }, AccountAssignComponent);
    } else {
      this.showCreateAccountConfirmOverlay();
    }
  }

  public showCreateAccountConfirmOverlay() {
    this.#overlayAlert.confirm(
      this.#translate.instant('ACCOUNTS.OVERLAYS.NO_ACCOUNT_CREATED.TITLE'),
      this.#translate.instant('ACCOUNTS.OVERLAYS.NO_ACCOUNT_CREATED.TEXT'),
      [
        {
          label: this.#translate.instant('cancel'),
          keyShortcut: 'Escape',
          mode: 'secondary',
          callback: (button, alert) => {
            alert.close();
            alert.callback.emit(false);
          }
        },
        {
          label: this.#translate.instant('ACCOUNTS.OVERLAYS.NO_ACCOUNT_CREATED.BUTTON_OK'),
          keyShortcut: 'Enter',
          mode: 'primary',
          callback: (button, alert) => {
            alert.close();
            alert.callback.emit(true);
          }
        }
      ]
    ).subscribe(_confirm => {
      if (_confirm) {
        this.showCreateAccountOverlay();
      }
    });
  }

  public showCreateAccountOverlay() {
    this.#overlayAlert.custom({
      headline: this.#translate.instant('account_new'),
      iconCls: 'icon-a147_add_larger',
      subheadline: this.#translate.instant('account_new_subline'),
      buttons: [
        {
          label: this.#translate.instant('cancel'),
          mode: 'secondary',
          keyShortcut: 'Escape',
          callback: (button, alert, childComponentRef) => childComponentRef?.instance.no()
        },
        {
          label: this.#translate.instant('create'),
          mode: 'primary',
          disabled: true,
          callback: (button, alert, childComponentRef) => childComponentRef?.instance.yes()
        }
      ],
      width: '1000px',
      advancedAlignmentMode: true,
      mandatoryLabel: true
    }, AccountAddComponent);
  }

  getProjectGroupedByAccount(_location_uuid: string): AutocompleteSuggestion[] {
    const grouped_data: AutocompleteSuggestion[] = [];

    if (this.isAccountProjectsLoaded$) {
      _.forEach(this.accountRelationsCache, (account: any) => {
        const t_group: typeof grouped_data[number] = {
          label: account.name,
          value: account.object_uuid,
          children: []
        };

        _.forEach(account.relations.projects, (_project: any) => {
          // Check if there is a Location and if so, we filter

          if (_project.location_uuid && _project.location_uuid == _location_uuid) {
            t_group.children!.push({ label: _project.name, value: _project.object_uuid });
          }
        });

        if (t_group.children!.length > 0) {
          grouped_data.push(t_group);
        }
      });
    }

    return grouped_data;
  }

  getProjectName(_project_uuid: string): string | undefined {
    const project: any = this.getProject(_project_uuid);
    if (project) {
      return project.name;
    }

    return undefined;
  }

  getProject(_project_uuid: string): AccountProjectRelation | undefined {
    if (this.projectsDataLoaded && this.projectsCache[_project_uuid]) {
      return this.projectsCache[_project_uuid];
    }

    return undefined;
  }

  getAccountByProject(_project_uuid: string): { name?: string } | undefined {
    const project: any = this.getProject(_project_uuid);
    if (project && this.accountRelationsCache[project.account_uuid]) {
      return this.accountRelationsCache[project.account_uuid];
    }

    return undefined;
  }

  /**
   * Returns the Account Object if it exsissts
   * @param account_uuid Account UUID
   */
  getAccountByUUID(account_uuid: string) {
    if (this.isAccountProjectsLoaded$ && this.accountRelationsCache[account_uuid]) {
      return this.accountRelationsCache[account_uuid];
    }

    return undefined;
  }

  /**
   * asks for confirmation for account lock
   * @param account_uuid
   * @param locked
   */
  lockOrUnlockAccount(account_uuid: string, locked: boolean) {
    this.#overlayAlert
      .confirm(
        locked ? this.#translate.instant('CONFIRM_LOCK_ACCOUNT.LOCK_HEADLINE') : this.#translate.instant('CONFIRM_LOCK_ACCOUNT.UNLOCK_HEADLINE'),
        locked ? this.#translate.instant('CONFIRM_LOCK_ACCOUNT.LOCK_BODY') : this.#translate.instant('CONFIRM_LOCK_ACCOUNT.UNLOCK_BODY'),
        [
          {
            label: this.#translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alertInstance) => {
              alertInstance.close();
            }
          },
          {
            label: locked ? this.#translate.instant('CONFIRM_LOCK_ACCOUNT.LOCK_CONFIRM') : this.#translate.instant('CONFIRM_LOCK_ACCOUNT.UNLOCK_CONFIRM'),
            mode: 'danger',
            callback: (button, alertInstance) => {
              alertInstance.close();

              this.#ngrxService.callBasicActionWithCallbacks({
                actionToCall: ContractActions.patchAccount({ account_uuid, data: { locked } }),
                successAction: ContractActions.patchAccountSuccess,
                failureAction: ContractActions.patchAccountFailure,
                successCallback: () => {
                  this.#toastService.add({ severity: 'success', summary: this.#translate.instant('account_is_locked') });
                },
                failureCallback: () => {
                  this.#toastService.add({ severity: 'error', summary: this.#translate.instant('CONFIRM_LOCK_ACCOUNT.ERROR') });
                }
              });
            }
          }
        ]
      );
  }



  /**
   * asks for confirmation for contract remove, also shows related users and provide to delete them also when they are only related to the current contract
   * @param account_uuid
   */
  removeAccount(account_uuid: string, onSucccess: () => void = () => { }) {
    this.isViewBusy$.next(true);
    const dataLoaded$: Subject<void> = new Subject();

    // listen for errors on loading relevant data
    this.#actions$
      .pipe(
        ofType(
          AccountActions.loadAccountFailure,
          AccountActions.loadAccountUsersFailure,
          StatsActions.loadContractResourcesFailure,
          UserActions.loadUserFailure
        ),
        take(1),
        takeUntil(dataLoaded$)
      )
      .subscribe(() => {
        this.isViewBusy$.next(false);
        this.errorOccured$.next();
        this.#toastService.add({ severity: 'error', summary: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.FAIL') });
      });

    // load the contract if not in store
    this.#store.select(AccountSelectors.isAccountLoaded({ uuid: account_uuid })).pipe(
      withLatestFrom(this.#store.select(AccountSelectors.isAccountLoading({ uuid: account_uuid }))),
      take(1)
    ).subscribe(([loaded, loading]) => {
      if (!loaded && !loading) {
        this.#store.dispatch(AccountActions.loadAccount({ account_uuid }));
      }
    });

    // load the contracts user relations if not in store
    this.#store.select(AccountSelectors.isAccountUsersLoaded({ uuid: account_uuid })).pipe(
      withLatestFrom(this.#store.select(AccountSelectors.isAccountUsersLoading({ uuid: account_uuid }))),
      take(1)
    ).subscribe(([loaded, loading]) => {
      if (!loaded && !loading) {
        this.#store.dispatch(AccountActions.loadAccountUsers({ account_uuid }))
      }
    });

    // load account resources if not in store
    this.#store.select(StatsSelectors.getAccountResourcesLoaded({ accountId: account_uuid })).pipe(
      withLatestFrom(this.#store.select(StatsSelectors.getAccountResourcesLoading({ accountId: account_uuid }))),
      take(1)
    ).subscribe(([loaded, loading]) => {
      if (!loaded && !loading) {
        this.#store.dispatch(StatsActions.loadAccountResources({accountId: account_uuid}));
      }
    });


    combineLatest([
      this.#store.select(AccountSelectors.getAccount({ uuid: account_uuid })),
      this.#store.select(AccountSelectors.getAccountUsers({ uuid: account_uuid })),
      this.#store.select(StatsSelectors.getAccountResources({accountId: account_uuid})),
      this.#store.select(CurrentUserSelectors.uuid)
    ]).pipe(
      filter(([contract, users, resources, currentUserUuid]) => contract !== undefined && users !== undefined && resources !== undefined),
      concatMap(([contract, users, resources, currentUserUuid]) => {
        const selects: Observable<Partial<User> | undefined>[] = [];
        _.forEach(users, user => {
          // check if the user is in store, otherwise load
          this.#store.select(UserSelectors.isUserLoaded({ uuid: user.user_uuid })).pipe(
            withLatestFrom(this.#store.select(UserSelectors.isUserLoading({ uuid: user.user_uuid }))),
            take(1)
          ).subscribe(([loaded, loading]) => {
            if (!loaded && !loading) {
              this.#store.dispatch(UserActions.loadUser({ user_uuid: user.user_uuid }));
            }
          });

          selects.push(this.#store.select(UserSelectors.getUser({ uuid: user.user_uuid })));
        });

        return combineLatest(selects).pipe(
          filter(userDetails => userDetails.indexOf(undefined) === -1),
          map(userDetails => [contract, users, resources, currentUserUuid, userDetails] as [typeof contract, typeof users, Partial<contractmanagement_api.ContractUsagesByProductNumber>, string, typeof userDetails])
        );
      }),
      take(1)
    ).subscribe(([contract, userRelations, resources, currentUserUuid, userDetails]) => {
      dataLoaded$.next();
      dataLoaded$.complete();
      this.isViewBusy$.next(false);
      const deletableUsers: (Partial<User> | undefined)[] = _.filter(userDetails!, user => user!.relations?.contracts?.length === 1 && user!.object_uuid !== currentUserUuid && user!.partner_role === null);
      const _resourcesRunning = Object.keys(resources).length > 0 ? this.#translate.instant('yes') : this.#translate.instant('no');

      const subUsers: Record<string, {
        selected: boolean;
        disabled?: boolean;
        dataColumns: (string | number | boolean)[];
        orderByDataColumn?: number;
      }> = {};

      _.forEach(deletableUsers, du => {
        if (du?.object_uuid) {
          const userRelation: any = _.find(userRelations, { user_uuid: du.object_uuid })

          subUsers[du.object_uuid] = {
            selected: false,
            dataColumns: [du.first_name + ' ' + du.last_name, _.capitalize(userRelation.user_role)]
          }
        }
      });

      this.#overlayAlert
        .confirmDelete({
          objectType: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.OBJECT_TYPE'),
          message: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SINGLE.MESSAGE'),
          subobjectLists: {
            'details': {
              listHeadlines: [this.#translate.instant('projects'), this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.TABLE.HEADERS.RUNNING_RESOURCES')],
              note: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.TABLE.NOTES.RUNNING_RESOURCES'),
              checkboxVisibility: CheckboxVisibility.HIDE,
              objects: {
                'detail': {
                  selected: false,
                  disabled: true,
                  dataColumns: [_.get(contract, ['relations', 'projects', 'length'], 0).toString(), _resourcesRunning],
                }
              }
            },
            'users': {
              intro: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SINGLE.INTRO'),
              listHeadlines: ['Name', this.#translate.instant('role')],
              objects: subUsers
            }
          },
          cannotBeUndone: true,
          cannotBeUndoneText: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.WARNING'),
          objectName: contract?.name || '',
          typeToConfirm: contract?.name
        })
        .subscribe(result => {
          if (result.confirmed) {
            this.#ngrxService.callBasicActionWithCallbacks({
              actionToCall: AccountActions.deleteAccount({ account_uuid }),
              successAction: AccountActions.deleteAccountSuccess,
              failureAction: AccountActions.deleteAccountFailure,
              successCallback: () => {
                this.#toastService.add({ severity: 'success', summary: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SINGLE.SUBMIT_MESSAGE.SUCCESS') });
                onSucccess();

                if (result.subobjectsSelected && result.subobjectsSelected['users']?.length) {
                  const deleteUserUuids = _.clone(result.subobjectsSelected['users']);

                  const jobId = this.#backgroundJobService.addJob({
                    status: JOB_STATUS.RUNNING,
                    text: this.#translate.instant('CONFIRM_DELETE.USER_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: '0 / ' + String(deleteUserUuids.length) }),
                    percentage: 0,
                  });

                  let deletedUserCount = 0;

                  const deleteUserDo = () => {
                    const deleteUserUuid = deleteUserUuids.shift();

                    if (deleteUserUuid) {
                      // delete with background job
                      this.#ngrxService.callBasicActionWithCallbacks({
                        actionToCall: UserActions.deleteUser({ user_uuid: deleteUserUuid }),
                        successAction: UserActions.deleteUserSuccess,
                        failureAction: UserActions.deleteUserFailure,
                        successCallback: () => {
                          ++deletedUserCount;

                          this.#backgroundJobService.updateJob(jobId, {
                            text: this.#translate.instant('CONFIRM_DELETE.USER_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: String(deletedUserCount) + ' / ' + String(result.subobjectsSelected['users'].length) }),
                            percentage: Math.round((deletedUserCount / result.subobjectsSelected['users'].length) * 100),
                          });

                          if (deletedUserCount < result.subobjectsSelected['users'].length) {
                            deleteUserDo();

                          } else {
                            // done
                            this.#backgroundJobService.updateJob(jobId, {
                              status: JOB_STATUS.SUCCESS
                            });
                          }
                        },
                        failureCallback: () => {
                          this.#backgroundJobService.updateJob(jobId, {
                            status: JOB_STATUS.FAILED
                          });
                        }
                      });
                    }
                  };

                  deleteUserDo();
                }

              },
              failureCallback: () => {
                this.#toastService.add({ severity: 'error', summary: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SINGLE.SUBMIT_MESSAGE.FAIL') });
              }
            });
          }
        });
    });

  }




  /**
   * request all resources for selected accounts and return the selectors combined
   * @param accounts
   * @returns
   */
  private loadSelectedAccountsResources$(accounts: Account[]) {
    const selectors: Record<string, Observable<Partial<contractmanagement_api.ContractUsagesByProductNumber | undefined>>> = {};

    accounts.forEach(account => {
      this.#store
        .select(StatsSelectors.getAccountResourcesLoaded({accountId: account.object_uuid}))
        .pipe(
          take(1),
          takeUntil(this.errorOccured$)
        )
        .subscribe((isLoaded) => {
          if (!isLoaded) {
            this.#store.dispatch(StatsActions.loadAccountResources({accountId: account.object_uuid}));
          }
        });

      selectors[account.object_uuid] = this.#store.select(StatsSelectors.getAccountResources({accountId: account.object_uuid})).pipe( filter((resources) => resources !== undefined));
    });

    return combineLatest(selectors);
  }

/*
  commented on 26.02.2024 because not in use (maybe remove at a later time)
  / **
   * request all selected users and return the selectors combined
   * @param userIds
   * @returns
   * /
  private loadAllRelatedUsersForSelectedAccounts$(userIds: string[]) {
    const selectors: Observable<Partial <User> | undefined>[] = [];

    userIds.forEach(uuid => {
      this.#store
        .select(UserSelectors.isUserLoaded({ uuid: uuid }))
        .pipe(
          withLatestFrom(this.#store.select(UserSelectors.isUserLoading({ uuid: uuid }))),
          take(1),
          takeUntil(this.errorOccured$)
        ).subscribe(([isLoaded, isLoading]) => {
          if (!isLoaded && !isLoading) {
            this.#store.dispatch(UserActions.loadUser({ user_uuid: uuid }));
          }
        });

      selectors.push(this.#store.select(UserSelectors.getUser({ uuid: uuid })).pipe( filter((user) => user !== undefined)));
    });

    return combineLatest(selectors);
  }


  / **
   * gather all users related to the accounts provided, request to load and return the selectors combined
   * @param accounts
   * @returns
   * /
  private loadSelectedAccountsUsers$(accounts: Account[]) {
    const selectors: Record<string, Observable<AccountUsersResponse['user_relations'] | undefined>> = {};


    accounts.forEach(account => {
      this.#store
        .select(AccountSelectors.isAccountUsersLoaded({ uuid: account.object_uuid }))
        .pipe(
          withLatestFrom(this.#store.select(AccountSelectors.isAccountUsersLoading({ uuid: account.object_uuid }))),
          take(1),
          takeUntil(this.errorOccured$)
        ).subscribe(([loaded, loading]) => {
          if (!loaded && !loading) {
            this.#store.dispatch(AccountActions.loadAccountUsers({ account_uuid: account.object_uuid }))
          }
      });
      selectors[account.object_uuid] = this.#store.select(AccountSelectors.getAccountUsers({uuid: account.object_uuid})).pipe( filter((users) => users !== undefined));
    });


    return combineLatest(selectors).pipe(
      withLatestFrom(this.#store.select(CurrentUserSelectors.uuid)),
      switchMap(([userRelations, currentUserUuid] : [Record<string, AccountUsersResponse['user_relations'] | undefined>, string]) => {
        const allRelatedUserUuids: string[] = [];
        Object.values(userRelations).forEach((relations)=>{
          relations?.forEach((relation) => {
            if ((relation.user_uuid !==currentUserUuid) && (allRelatedUserUuids.indexOf(relation.user_uuid) === -1)) {
              allRelatedUserUuids.push(relation.user_uuid);
            }
          })
        });
        return this.loadAllRelatedUsersForSelectedAccounts$(allRelatedUserUuids);
      })
    )

  }


  / **
   * given an array of account uuids return a string with their names
   * @param uuids
   * @param accounts
   * @returns
   * /
  private getLinkedAccountsAsString(uuids: string[], accounts: Account[]) {
    const accountList: string[] = [];

    uuids.forEach((uuid:string)=> {
      const _account = accounts.find((account) => {
        return account.object_uuid === uuid;

      });
      if (_account) {
        accountList.push(_account.name);
      }
    });
    return accountList.join(', ');
  }


  / **
   * filter related users: keep only those that are linked to the accounts provided, but not to the partner panel
   * returns as object ready for confirmDelete overlay subobjectLists
   * @param users
   * @param accounts
   * @returns
   * /
  private filterUsersToDelete(users: User[], accounts: Account[]) {
    const usersToDelete: NonNullable<ExtendedConfirmationOptions['subobjectLists']>[string]['objects'] = {};
    const accountUuids: string[] = accounts.map(_a => _a.object_uuid);

    users.forEach((user)=> {
      const relatedAccounts = user.relations?.contracts?.map(_a => _a.contract_uuid);
      if ((relatedAccounts?.every((uuid) => accountUuids.includes(uuid))) && (user.partner_role === null)) {
        usersToDelete[user.object_uuid] = {
          selected: false,
          disabled: false,
          dataColumns: [user.first_name + ' ' + user.last_name, this.getLinkedAccountsAsString(relatedAccounts, accounts)]
        }
      }
    });

    return usersToDelete;
  }
*/

  /**
   * build accounts object ready for confirmDelete overlay, with current resources usage included
   * @param resources
   * @param accounts
   * @returns
   */
  private addResourceUsageToSelectedAccounts(resources: Record<string, contractmanagement_api.ContractUsagesByProductNumber>, accounts: Account[]) {
    const accountsToDelete: NonNullable<ExtendedConfirmationOptions['subobjectLists']>[string]['objects'] = {};

    accounts.forEach(_account => {
      const _resourcesRunning = Object.keys(_.get(resources, _account.object_uuid, {})).length > 0 ? this.#translate.instant('yes') : this.#translate.instant('no');
      accountsToDelete[_account.object_uuid] = {
        selected: true,
        disabled: false,
        dataColumns: [_account.name, _account.relations.projects.length.toString(), _resourcesRunning]
      }
    });

    return accountsToDelete;
  }


  /**
   * gather all necesary data (current resources usage, related users that are linked only to these accounts) for the selected accounts
   * returns data ready for confirmDelete overlay
   * @param accounts
   * @param withRelatedUsers if related user should also show up in the overlay; not in use at this moment
   * @returns
   */
  private loadAccountsData$(accounts: Account[], withRelatedUsers: boolean = false) {
    if (withRelatedUsers) {

      // not in use at this point

      return of({
        accounts: {},
        users: {}
      });
      /*
      return combineLatest([
        this.loadSelectedAccountsUsers$(accounts),
        this.loadSelectedAccountsResources$(accounts)
      ]).pipe(
        take(1),
        takeUntil(this.errorOccured$),
        map(([users, resources]: [User[], Record<string, ContractUsagesByProductNumber>]) => {
          return {
            accounts: this.addResourceUsageToSelectedAccounts(resources, accounts),
            users: this.filterUsersToDelete(users, accounts)
          };
        })
      )
      */
    } else {
      return combineLatest([
        this.loadSelectedAccountsResources$(accounts)
      ]).pipe(
        take(1),
        takeUntil(this.errorOccured$),
        map(([resources]: [Record<string, contractmanagement_api.ContractUsagesByProductNumber>]) => {
          return {
            accounts: this.addResourceUsageToSelectedAccounts(resources, accounts),
            users: {}
          };
        })
      )
    }
  }


  /**
   * asks for confirmation on deleting multiple accounts
   * provides indication if there is current resource usage and also users that are linked exclusively to these accounts
   * @param selected_accounts
   */
  removeAccounts(selected_accounts: Account[], onSucccess: () => void = () => { }) {
    this.isViewBusy$.next(true);
    const dataLoaded$: Subject<void> = new Subject();

    // listen for errors on loading relevant data
    this.#actions$
      .pipe(
        ofType(
          StatsActions.loadContractResourcesFailure,
          AccountActions.loadAccountUsersFailure,
          UserActions.loadUserFailure
        ),
        take(1),
        takeUntil(dataLoaded$)
      )
      .subscribe(() => {
        this.isViewBusy$.next(false);
        this.errorOccured$.next();
        this.#toastService.add({ severity: 'error', summary: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.FAIL') });
      });



    this.loadAccountsData$(selected_accounts, false)
    .pipe(
      take(1),
      takeUntil(this.errorOccured$)
    )
    .subscribe((result)=> {
      this.isViewBusy$.next(false);

      dataLoaded$.next();
      dataLoaded$.complete();

      this.#overlayAlert
        .confirmDelete({
          objectType: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.OBJECT_TYPE'),
          objectName: 'Accounts',
          customHeadline: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.HEADLINE', { number: selected_accounts.length }),
          message: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.MESSAGE', { number: selected_accounts.length }),
          subobjectLists: {
            'accounts': {
              listHeadlines: [this.#translate.instant('account_name'), this.#translate.instant('projects'), this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.TABLE.HEADERS.RUNNING_RESOURCES')],
              note: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.TABLE.NOTES.RUNNING_RESOURCES'),
              objects: result.accounts
            },
            'users': {
              intro: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.TABLE.INTROS.USERS'),
              listHeadlines: [this.#translate.instant('name'), this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.TABLE.HEADERS.LINKED_TO')],
              objects: result.users
            }
          },
          cannotBeUndone: true,
          cannotBeUndoneText: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.WARNING'),
          typeToConfirm: this.#translate.instant('CONFIRM_DELETE.CONFIRM'),
          width: '600px'
        },[
          {
            label: this.#translate.instant('CONFIRM_DELETE.BUTTONS.KEEP'),
            keyShortcut: 'Escape',
            mode: 'secondary',
            callback: (button, alert) => {
              alert.callback.emit(false);
              alert.close();
            }
          },
          {
            label: this.#translate.instant('CONFIRM_DELETE.BUTTONS.DELETE'),
            mode: 'danger',
            callback: (button, alert) => {
              alert.callback.emit(true);
              alert.close();
            }
          }
        ])
        .subscribe(result => {
          if (result.confirmed) {

            if (result.subobjectsSelected && result.subobjectsSelected['accounts']?.length) {

              const accountsUuidToDelete = [...result.subobjectsSelected['accounts']];
              const jobId = this.#backgroundJobService.addJob({
                status: JOB_STATUS.RUNNING,
                text: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: '0 / ' + String(accountsUuidToDelete.length), account_name: '' }),
                percentage: 0,
              });

              let _deletedAccounts = 0;

              const deleteAccounts = () => {
                const _accountUuid = accountsUuidToDelete.shift();
                const _accountName = selected_accounts.find((account) =>  account.object_uuid === _accountUuid)?.name;

                if (_accountUuid) {
                  // delete with background job
                  this.#ngrxService.callBasicActionWithCallbacks({
                    actionToCall: AccountActions.deleteAccount({ account_uuid: _accountUuid }),
                    successAction: AccountActions.deleteAccountSuccess,
                    failureAction: AccountActions.deleteAccountFailure,
                    successCallback: () => {
                      ++_deletedAccounts;

                      this.#backgroundJobService.updateJob(jobId, {
                        text: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: String(_deletedAccounts) + ' / ' + String(result.subobjectsSelected['accounts'].length), account_name: _accountName }),
                        percentage: Math.round((_deletedAccounts / result.subobjectsSelected['accounts'].length) * 100),
                      });

                      if (_deletedAccounts < result.subobjectsSelected['accounts'].length) {
                        deleteAccounts();

                      } else {
                        // done
                        this.#backgroundJobService.updateJob(jobId, {
                          status: JOB_STATUS.SUCCESS,
                          text: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: String(_deletedAccounts) + ' / ' + String(result.subobjectsSelected['accounts'].length), account_name: '' }),
                        });
                        this.#toastService.add({ severity: 'success', summary: this.#translate.instant('CONFIRM_DELETE.ACCOUNT_DELETE.SELECTION.SUBMIT_MESSAGE.SUCCESS') });
                        onSucccess();
                      }
                    },
                    failureCallback: () => {
                      this.#backgroundJobService.updateJob(jobId, {
                        status: JOB_STATUS.FAILED
                      });
                    }
                  });
                }
              };

              deleteAccounts();

            }


            if (result.subobjectsSelected && result.subobjectsSelected['users']?.length) {

              const usersUuidToDelete = [...result.subobjectsSelected['users']];
              const jobId2 = this.#backgroundJobService.addJob({
                status: JOB_STATUS.RUNNING,
                text: this.#translate.instant('CONFIRM_DELETE.USER_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: '0 / ' + String(usersUuidToDelete.length) }),
                percentage: 0,
              });

              let _deletedUsers = 0;

              const deleteUsers = () => {
                const _userUuid = usersUuidToDelete.shift();

                if (_userUuid) {
                  // delete with background job
                  this.#ngrxService.callBasicActionWithCallbacks({
                    actionToCall: UserActions.deleteUser({ user_uuid: _userUuid }),
                    successAction: UserActions.deleteUserSuccess,
                    failureAction: UserActions.deleteUserFailure,
                    successCallback: () => {
                      ++_deletedUsers;

                      this.#backgroundJobService.updateJob(jobId2, {
                        text: this.#translate.instant('CONFIRM_DELETE.USER_DELETE.SELECTION.SUBMIT_MESSAGE.PROGRESS', { progress: String(_deletedUsers) + ' / ' + String(result.subobjectsSelected['users'].length) }),
                        percentage: Math.round((_deletedUsers / result.subobjectsSelected['users'].length) * 100)
                      });

                      if (_deletedUsers < result.subobjectsSelected['users'].length) {
                        deleteUsers();

                      } else {
                        // done
                        this.#backgroundJobService.updateJob(jobId2, {
                          status: JOB_STATUS.SUCCESS
                        });
                        this.#toastService.add({ severity: 'success', summary: this.#translate.instant('CONFIRM_DELETE.USER_DELETE.SELECTION.SUBMIT_MESSAGE.SUCCESS') });
                        onSucccess();
                      }
                    },
                    failureCallback: () => {
                      this.#backgroundJobService.updateJob(jobId2, {
                        status: JOB_STATUS.FAILED
                      });
                    }
                  });
                }
              };

              deleteUsers();

            }
          }
        });

    })

  }


  offlineAccount(account_uuid: string) {
    // switch account to offline
    this.#overlayAlert.confirm(
      this.#translate.instant('account_switch_offline_confirm_title'),
      this.#translate.instant('account_switch_offline_confirm_text'),
      [
        {
          label: this.#translate.instant('cancel'),
          mode: 'secondary',
          callback: (button, alert) => {
            alert.close();
          }
        },
        {
          label: this.#translate.instant('account_switch_offline'),
          mode: 'danger',
          callback: (button, alert) => {
            alert.loading = true;
            this.#ngrxService.callBasicActionWithCallbacks({
              actionToCall: AccountActions.patchContractDisconnected({ account_uuid, data: { disconnected: true } }),
              successAction: AccountActions.patchContractDisconnectedSuccess,
              failureAction: AccountActions.patchContractDisconnectedFailure,
              successCallback: () => {
                alert.close();
                this.#toastService.add({
                  severity: 'success',
                  summary: this.#translate.instant('account_switch_offline_success')
                })
              },
              failureCallback: () => {
                alert.loading = true;
              }
            })
          }
        }
      ]
    )
  }

  onlineAccount(account_uuid: string) {
    // switch account to online

    this.#overlayAlert.confirm(
      this.#translate.instant('account_switch_online_confirm_title'),
      this.#translate.instant('account_switch_online_confirm_text'),
      [
        {
          label: this.#translate.instant('cancel'),
          mode: 'secondary',
          callback: (button, alert) => {
            alert.close();
          }
        },
        {
          label: this.#translate.instant('account_switch_online'),
          mode: 'primary',
          callback: (button, alert) => {
            alert.loading = true;
            this.#ngrxService.callBasicActionWithCallbacks({
              actionToCall: AccountActions.patchContractDisconnected({ account_uuid, data: { disconnected: false } }),
              successAction: AccountActions.patchContractDisconnectedSuccess,
              failureAction: AccountActions.patchContractDisconnectedFailure,
              successCallback: () => {
                alert.close();
                this.#toastService.add({
                  severity: 'success',
                  summary: this.#translate.instant('account_switch_online_success')
                })
              },
              failureCallback: () => {
                alert.loading = false;
              }
            })
          }
        }
      ]
    )
  }


  ngOnDestroy() {
    this.errorOccured$.next();
    this.errorOccured$.complete();
  }
}
